/**
 * ProShip Direct — changing plan.
 *
 * Two roads, and the direction decides which:
 *
 *   UP, NOW. Charged today for the price gap over the days left in the period, with the full
 *   difference in shipments added at once (the merchant upgrading mid-period is the one who ran
 *   out early). The renewal date does not move. The figures come from the server's quote, so what
 *   is shown is what is charged, and the payment needs the code from the registered email.
 *
 *   ANY PLAN, FROM THE NEXT RENEWAL. The only way to a lower plan: nothing already paid for is
 *   taken away mid-period. Nothing is charged today. When the new plan allows fewer courier
 *   accounts than are switched on, the merchant picks the ones to keep; the rest are switched off
 *   (not retired) when the new period starts.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Divider, FormControlLabel, Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';

import MUIModal from '../../../common/MUIModal';
import ActivationOtpStep from './ActivationOtpStep';
import { primaryBtnSx, secondaryBtnSx } from '../../../styles/patterns';
import { SUBTLE, NAVY, BORDER, LIGHT_BG, SUCCESS, WARNING, PRIMARY_BLUE } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { GAP, RADIUS } from '../../../styles/foundation/spacing';
import { formatDate } from '../../../helpers/UtilityHelper';
import { statLabelSx } from './stats';
import { OTP_LENGTH } from '../data/activationOtp';
import useActivationOtp from '../data/useActivationOtp';
import { fetchMyCouriers } from '../data/api';

const smallSx = { fontFamily: FONT, fontSize: SIZE['3xs'], color: SUBTLE, lineHeight: 1.5 };
const strongSx = { fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: NAVY };
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const count = (n) => (n === null || n === undefined ? 'Unlimited' : Number(n).toLocaleString('en-IN'));
const day = (d) => (d ? formatDate(d, 'dd MMM yyyy') : 'renewal');

/** One plan to move to, picked by clicking it. */
function PlanOption({ plan, tag, selected, onSelect }) {
  return (
    <Box
      onClick={onSelect}
      role='button'
      tabIndex={0}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect()}
      sx={{
        flex: 1,
        minWidth: 180,
        p: 1.25,
        cursor: 'pointer',
        border: `1px solid ${selected ? PRIMARY_BLUE : BORDER}`,
        bgcolor: selected ? `${PRIMARY_BLUE}0a` : 'transparent',
        borderRadius: RADIUS.md,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Typography sx={strongSx}>{plan.name}</Typography>
        <Chip
          size='small'
          label={tag}
          sx={{ height: 17, fontFamily: FONT, fontSize: SIZE['4xs'], bgcolor: `${PRIMARY_BLUE}14`, color: PRIMARY_BLUE }}
        />
      </Box>
      <Typography sx={{ ...smallSx, mt: 0.25 }}>
        {inr(plan.price)} + GST · {count(plan.shipments)} shipments
      </Typography>
      <Typography sx={smallSx}>{count(plan.courierIntegrations)} courier accounts</Typography>
    </Box>
  );
}

/** Upgrade now or from renewal, for a higher plan. */
function ModeOption({ title, sub, selected, onSelect }) {
  return (
    <Box
      onClick={onSelect}
      role='button'
      tabIndex={0}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect()}
      sx={{
        flex: 1,
        minWidth: 180,
        p: 1,
        cursor: 'pointer',
        border: `1px solid ${selected ? PRIMARY_BLUE : BORDER}`,
        bgcolor: selected ? `${PRIMARY_BLUE}0a` : 'transparent',
        borderRadius: RADIUS.md,
      }}
    >
      <Typography sx={strongSx}>{title}</Typography>
      <Typography sx={smallSx}>{sub}</Typography>
    </Box>
  );
}

export default function PlanChangeModal({
  plan, usage, catalog, billing, quoteUpgrade, onUpgrade, onSetRenewalPlan, onClearRenewalPlan, onClose,
}) {
  const current = usage.plan;
  const currentFee = Number(current?.price || 0);
  const canUpgradeNow = plan.status === 'ACTIVE' && plan.paidFrom !== 'EXTERNAL';
  // A trial does not renew, so there is no "from next renewal": moving on from it is upgrading now.
  const canSchedule = !plan.trial;
  const options = useMemo(
    () => catalog.filter((c) => c.id !== plan.planId && (canSchedule || Number(c.price) > Number(usage.plan?.price || 0))),
    [catalog, plan.planId, canSchedule, usage.plan],
  );

  const [targetId, setTargetId] = useState(null);
  const [mode, setMode] = useState('RENEWAL');
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [couriers, setCouriers] = useState([]);
  const [keep, setKeep] = useState([]);
  const [step, setStep] = useState('CHOOSE');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const otp = useActivationOtp();

  const target = options.find((o) => o.id === targetId) || null;
  const isHigher = target ? Number(target.price) > currentFee : false;

  useEffect(() => {
    fetchMyCouriers()
      .then((res) => setCouriers(Array.isArray(res?.data) ? res.data : []))
      .catch(() => setCouriers([]));
  }, []);

  const choose = (option) => {
    setTargetId(option.id);
    setMode(Number(option.price) > currentFee && canUpgradeNow ? 'NOW' : 'RENEWAL');
    setKeep([]);
    setError(null);
  };

  useEffect(() => {
    if (!target || mode !== 'NOW') {
      setQuote(null);
      return;
    }
    let alive = true;
    setQuoteLoading(true);
    quoteUpgrade(target.id)
      .then((q) => { if (alive) setQuote(q); })
      .catch((e) => { if (alive) { setQuote(null); setError(e.message); } })
      .finally(() => { if (alive) setQuoteLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetId, mode]);

  const active = couriers.filter((c) => c.isActive && !c.retired);
  const limit = target?.courierIntegrations ?? null;
  const needKeep = mode === 'RENEWAL' && limit !== null && active.length > limit;

  const payFromWallet = !billing.isPostpaid;
  const shortfall = quote && payFromWallet ? Math.max(Number(quote.total) - billing.walletBalance, 0) : 0;
  const blocked = mode === 'NOW' && payFromWallet && shortfall > 0;

  const toggleKeep = (code) =>
    setKeep((k) => (k.includes(code) ? k.filter((c) => c !== code) : k.length < limit ? [...k, code] : k));

  const act = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onClose();
    } catch (e) {
      setError(e?.message || 'That did not go through. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const continueToPay = async () => {
    setError(null);
    if (await otp.send()) setStep('VERIFY');
  };

  // The code travels with the upgrade and is checked before the charge; a wrong one stays on this step.
  const verifyAndPay = async () => {
    const proof = otp.proof();
    if (proof) await act(() => onUpgrade(target.id, proof));
  };

  const switchAtRenewal = () => act(() => onSetRenewalPlan(target.id, needKeep ? keep : null));
  const keepCurrent = () => act(() => onClearRenewalPlan());

  // ── footer ──
  let primary = null;
  if (step === 'VERIFY') {
    primary = (
      <Button size='small' variant='contained' sx={primaryBtnSx}
              disabled={busy || otp.sending || otp.entered.length !== OTP_LENGTH} onClick={verifyAndPay}
              startIcon={busy ? <CircularProgress size={12} /> : null}>
        {busy ? 'Charging…' : `Verify and pay ${inr(quote?.total)}`}
      </Button>
    );
  } else if (target && mode === 'NOW') {
    primary = (
      <Button size='small' variant='contained' sx={primaryBtnSx}
              disabled={!quote || quoteLoading || blocked || otp.sending} onClick={continueToPay}
              startIcon={otp.sending ? <CircularProgress size={12} /> : null}>
        {otp.sending ? 'Sending code' : quote ? `Continue to pay ${inr(quote.total)}` : 'Continue'}
      </Button>
    );
  } else if (target && canSchedule) {
    primary = (
      <Button size='small' variant='contained' sx={primaryBtnSx}
              disabled={busy || (needKeep && keep.length === 0)} onClick={switchAtRenewal}
              startIcon={busy ? <CircularProgress size={12} /> : null}>
        Switch from {day(plan.renewsOn || plan.periodEnd)}
      </Button>
    );
  }

  return (
    <MUIModal
      open
      handleClose={() => { if (!busy) onClose(); }}
      maxWidth='sm'
      title={step === 'VERIFY' ? 'Confirm your upgrade' : 'Change plan'}
      action={
        <Box sx={{ display: 'flex', gap: GAP.sm }}>
          <Button size='small' sx={secondaryBtnSx} disabled={busy}
                  onClick={step === 'VERIFY' ? () => setStep('CHOOSE') : onClose}>
            {step === 'VERIFY' ? 'Back' : 'Cancel'}
          </Button>
          {primary}
        </Box>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 0.5 }}>
        {(error || (step === 'CHOOSE' && otp.error)) && <Alert severity='error'>{error || otp.error}</Alert>}

        {step === 'VERIFY' ? (
          <>
            <Typography sx={smallSx}>
              Moving to the {target?.name} plan now. {inr(quote?.total)} (including GST) will be{' '}
              {billing.isPostpaid ? 'added to your invoice' : 'debited from your wallet'}.
            </Typography>
            <ActivationOtpStep {...otp.stepProps} />
          </>
        ) : (
          <>
            {plan.nextPlanName && (
              <Alert
                severity='info'
                action={
                  <Button size='small' sx={secondaryBtnSx} disabled={busy} onClick={keepCurrent}>
                    Keep {current?.name || 'current plan'}
                  </Button>
                }
              >
                From {day(plan.renewsOn)} your plan moves to {plan.nextPlanName}.
              </Alert>
            )}

            {options.length === 0 ? (
              <Typography sx={smallSx}>There are no other plans to move to right now.</Typography>
            ) : (
              <Box>
                <Typography sx={{ ...statLabelSx, mb: 0.5 }}>Move to</Typography>
                <Box sx={{ display: 'flex', gap: GAP.sm, flexWrap: 'wrap' }}>
                  {options.map((o) => (
                    <PlanOption key={o.id} plan={o} selected={o.id === targetId} onSelect={() => choose(o)}
                                tag={Number(o.price) > currentFee ? 'Higher' : 'Lower'} />
                  ))}
                </Box>
              </Box>
            )}

            {plan.trial && !canUpgradeNow && (
              <Alert severity='info'>Your trial has ended. Choose a plan on the ProShip Direct page once it expires.</Alert>
            )}

            {target && isHigher && canUpgradeNow && canSchedule && (
              <Box sx={{ display: 'flex', gap: GAP.sm, flexWrap: 'wrap' }}>
                <ModeOption title='Upgrade now' sub='Pay the difference for the days left'
                            selected={mode === 'NOW'} onSelect={() => setMode('NOW')} />
                <ModeOption title={`From ${day(plan.renewsOn || plan.periodEnd)}`} sub='Nothing to pay today'
                            selected={mode === 'RENEWAL'} onSelect={() => setMode('RENEWAL')} />
              </Box>
            )}

            {target && mode === 'NOW' && (
              quoteLoading || !quote ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.sm }}>
                  {quoteLoading && <CircularProgress size={14} />}
                  <Typography sx={smallSx}>{quoteLoading ? 'Working out the charge…' : ''}</Typography>
                </Box>
              ) : (
                <>
                  <Box sx={{ p: 1.25, bgcolor: `${SUCCESS}0a`, border: `1px solid ${SUCCESS}33`, borderRadius: RADIUS.md }}>
                    <Typography sx={strongSx}>
                      {quote.extraShipments === null ? 'Unlimited shipments' : `+ ${count(quote.extraShipments)} shipments`}
                      {quote.newCourierLimit !== (current?.courierIntegrations ?? null)
                        ? `, ${count(quote.newCourierLimit)} courier accounts` : ''}
                    </Typography>
                    <Typography sx={smallSx}>
                      Added in full to this period, usable until {day(quote.periodEnd)}.
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1.25, bgcolor: LIGHT_BG, borderRadius: RADIUS.md }}>
                    {[
                      { k: `${quote.toPlanName} plan`, v: inr(quote.newFee) },
                      { k: `Less your ${current?.name || quote.fromPlanCode} plan`, v: `− ${inr(quote.currentFee)}` },
                      { k: `For ${quote.daysRemaining} of ${quote.daysInPeriod} days left`, v: inr(quote.base) },
                      { k: `GST (${Math.round(Number(quote.gstRate) * 100)}%)`, v: inr(quote.gst) },
                    ].map((row) => (
                      <Box key={row.k} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, py: 0.25 }}>
                        <Typography sx={smallSx}>{row.k}</Typography>
                        <Typography sx={{ ...smallSx, color: NAVY }}>{row.v}</Typography>
                      </Box>
                    ))}
                    <Divider sx={{ my: 0.75, borderColor: BORDER }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                      <Typography sx={strongSx}>Payable now</Typography>
                      <Typography sx={strongSx}>{inr(quote.total)}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.sm }}>
                    {billing.isPostpaid ? (
                      <>
                        <ReceiptLongOutlinedIcon sx={{ fontSize: 17, color: PRIMARY_BLUE }} />
                        <Typography sx={smallSx}>Added to your postpaid invoice.</Typography>
                      </>
                    ) : (
                      <>
                        <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 17, color: blocked ? WARNING : SUCCESS }} />
                        <Typography sx={smallSx}>Wallet balance {inr(billing.walletBalance)}</Typography>
                      </>
                    )}
                  </Box>
                  {blocked && (
                    <Alert severity='warning'
                           action={<Button component={Link} to='/wallet' size='small' sx={secondaryBtnSx}>Recharge wallet</Button>}>
                      Your wallet is {inr(shortfall)} short.
                    </Alert>
                  )}
                  <Typography sx={smallSx}>
                    Your renewal date stays {day(quote.periodEnd)}. From then the {quote.toPlanName} plan is charged
                    in full, {inr(quote.newFee)} plus GST, while auto-renew is on.
                  </Typography>
                </>
              )
            )}

            {target && mode === 'RENEWAL' && canSchedule && (
              <>
                <Box sx={{ p: 1.25, bgcolor: LIGHT_BG, borderRadius: RADIUS.md }}>
                  <Typography sx={strongSx}>
                    From {day(plan.renewsOn || plan.periodEnd)}: {target.name}, {inr(target.price)} plus GST
                  </Typography>
                  <Typography sx={smallSx}>
                    {count(target.shipments)} shipments and {count(target.courierIntegrations)} courier accounts per period.
                    Nothing is charged today, and your current plan runs unchanged until then.
                    {plan.autoRenew ? '' : ' Auto-renew will be switched on so the new plan starts.'}
                  </Typography>
                </Box>
                {needKeep && (
                  <Box>
                    <Typography sx={{ ...statLabelSx, mb: 0.5 }}>
                      Choose up to {limit} courier account{limit === 1 ? '' : 's'} to keep
                    </Typography>
                    <Typography sx={{ ...smallSx, mb: 0.5 }}>
                      You have {active.length} switched on. The others are switched off when the new plan starts;
                      you can switch them back on later if your plan allows.
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      {active.map((c) => (
                        <FormControlLabel
                          key={c.accountCode}
                          control={<Checkbox size='small' checked={keep.includes(c.accountCode)}
                                             disabled={!keep.includes(c.accountCode) && keep.length >= limit}
                                             onChange={() => toggleKeep(c.accountCode)} />}
                          label={<Typography sx={smallSx}>{c.displayName || c.accountCode} ({c.courierCode})</Typography>}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Box>
    </MUIModal>
  );
}
